import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "@/src/services/api/axios";
import gqlClient from "@/src/services/api/graphql.client";
import { decodeToken } from "@/src/services/api/auth.api";
import {
  CREATE_GROUP_MEMBERSHIP_MUTATION,
  CREATE_GROUP_MUTATION,
  DELETE_GROUP_MEMBERSHIP_MUTATION,
  DELETE_GROUP_MUTATION,
  DELETE_MANY_GROUP_MEMBERSHIP_BY_GROUP,
  GET_GROUP_MEMBER_COUNT,
  GET_GROUP_MEMBERS_QUERY,
  GET_GROUPS_QUERY,
  UPDATE_GROUP_MEMBERSHIP_MUTATION,
  UPDATE_GROUP_MUTATION,
} from "./groups.query";
import { DELETE_MANY_POST_BY_GROUP } from "@/src/services/api/posts/posts.query";
import {
  BlocksUserListItem,
  GroupMember,
  GroupMemberRole,
  InvitableMember,
} from "@/src/models/user.model";
import { MembershipStatus } from "@/src/constants/groups/group-membership";
import { GroupListItem } from "@/src/models/group.model";
import { fetchBlocksUsersByIds } from "@/src/services/users.blocks.api";

export type GroupMemberAvatar = Readonly<{
  userId: string;
  profileImageUrl: string | null;
  name: string;
}>;

export const useGetUsers = (searchTerm: string = "") => {
  return useInfiniteQuery({
    queryKey: ["getUsers", searchTerm?.trim()],
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const currentOrgId = (await decodeToken())?.org_id;

      return apiClient
        .post(process.env.EXPO_PUBLIC_GET_USERS ?? "", {
          page: pageParam,
          pageSize: 10,
          projectKey: process.env.EXPO_PUBLIC_X_BLOCKS_KEY,
          filter: {
            name: searchTerm?.trim() || "",
            email: "",
            status: { active: true, inactive: false },
            ...(currentOrgId && { organizationId: currentOrgId }),
          },
        })
        .then((res) => {
          const users = res.data.data.map(
            (user: BlocksUserListItem): InvitableMember => ({
              itemId: user.itemId,
              userName: user.userName ?? "Unknown",
              firstName: user.firstName ?? "",
              lastName: user.lastName ?? "",
              displayName:
                user.firstName || user.lastName
                  ? `${user.firstName} ${user.lastName}`.trim()
                  : user.userName,
              profileImageUrl: user.profileImageUrl,
              email: user.email,
            }),
          );

          return {
            users,
            currentPage: pageParam as number,
            hasMore: res.data.data.length > 0,
            count: res.data.totalCount,
          };
        });
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
  });
};

export const useGetGroups = (pageSize = 4) => {
  return useInfiniteQuery({
    queryKey: ["getGroups"],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUPS_QUERY, {
        filter: "{}",
        sort: "{}",
        pageNo: pageParam,
        pageSize,
      });
      return result;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.getgroups?.hasNextPage ? allPages.length + 1 : undefined,
  });
};

export const useGetLatestGroups = (pageSize = 5) => {
  return useQuery({
    queryKey: ["getLatestGroups", pageSize],
    queryFn: async () => {
      const client = await gqlClient();
      const result: any = await client.request(GET_GROUPS_QUERY, {
        filter: "{}",
        sort: JSON.stringify({ CreatedDate: -1 }),
        pageNo: 1,
        pageSize,
      });
      return (result?.getgroups?.items ?? []) as GroupListItem[];
    },
  });
};

export const useGetUserGroupMemberships = (userId?: string) => {
  return useQuery({
    queryKey: ["getUserGroupMemberships", userId],
    queryFn: async () => {
      const client = await gqlClient();
      const result: any = await client.request(GET_GROUP_MEMBERS_QUERY, {
        filter: JSON.stringify({
          userId,
          status: MembershipStatus.ACTIVE,
        }),
        sort: "{}",
        pageNo: 1,
        pageSize: 200,
      });
      return (
        (result?.getgroupmemberships?.items ?? []) as { groupId: string }[]
      )
        .map((membership) => membership.groupId)
        .filter(Boolean);
    },
    enabled: !!userId,
  });
};

export const useCreateGroup = () => {
  return useMutation({
    mutationFn: async (variables: {
      name: string;
      description: string;
      coverImageUrl?: string;
      isPrivate: boolean;
    }) => {
      const client = await gqlClient();
      return client.request(CREATE_GROUP_MUTATION, variables);
    },
  });
};

export const useCreateGroupMembership = () => {
  return useMutation({
    mutationFn: async (variables: {
      groupId: string;
      userId: string;
      role: GroupMemberRole;
      status: string;
      joinType: string;
      joinedAt: string;
      invitedBy?: string;
      groupUserSnapshot: {
        firstName: string;
        lastName: string;
        email: string;
        profileImageUrl?: string;
      };
    }) => {
      const client = await gqlClient();
      return client.request(CREATE_GROUP_MEMBERSHIP_MUTATION, variables);
    },
  });
};

export const useRemoveGroupMembership = () => {
  return useMutation({
    mutationFn: async (membershipId: string) => {
      const client = await gqlClient();
      return client.request(DELETE_GROUP_MEMBERSHIP_MUTATION, {
        filter: JSON.stringify({ ItemId: membershipId }),
      });
    },
  });
};

export const useDeleteGroup = () => {
  return useMutation({
    mutationFn: async (groupId: string) => {
      const client = await gqlClient();

      // 1. Delete every post shared in this group (bulk).
      await client.request(DELETE_MANY_POST_BY_GROUP, { groupId });

      // 2. Delete every membership of this group (bulk).
      await client.request(DELETE_MANY_GROUP_MEMBERSHIP_BY_GROUP, { groupId });

      // 3. Delete the group itself (single record, by id).
      return client.request(DELETE_GROUP_MUTATION, {
        filter: JSON.stringify({ ItemId: groupId }),
      });
    },
  });
};

export const useUpdateGroup = () => {
  return useMutation({
    mutationFn: async (variables: {
      filter?: string;
      name?: string;
      description?: string;
      coverImageUrl?: string;
      isPrivate: boolean;
    }) => {
      const client = await gqlClient();
      return client.request(UPDATE_GROUP_MUTATION, variables);
    },
  });
};

export const useGetGroupById = (id: string) => {
  return useQuery({
    queryKey: ["getGroupById", id],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUPS_QUERY, {
        filter: JSON.stringify({ ItemId: `${id}` }),
      });
      return result.getgroups.items[0];
    },
    enabled: !!id,
  });
};

export const useGetGroupMembers = (
  groupId: string,
  page = 1,
  pageSize = 10,
) => {
  return useQuery({
    queryKey: ["getGroupMembers", groupId, page],
    queryFn: async () => {
      const client = await gqlClient();
      return client.request(GET_GROUP_MEMBERS_QUERY, {
        filter: JSON.stringify({ groupId }),
        sort: "{}",
        pageNo: page,
        pageSize,
      });
    },
  });
};

export const useGetGroupMemberIds = (groupId: string) => {
  return useQuery({
    queryKey: ["getGroupMemberIds", groupId],
    queryFn: async (): Promise<string[]> => {
      const client = await gqlClient();
      const result: any = await client.request(GET_GROUP_MEMBERS_QUERY, {
        filter: JSON.stringify({ groupId }),
        sort: "{}",
        pageNo: 1,
        pageSize: 1000,
      });
      const items = (result?.getgroupmemberships?.items ?? []) as {
        userId: string;
      }[];
      return items.map((item) => item.userId).filter(Boolean);
    },
    enabled: !!groupId,
  });
};

export const useUpdateGroupMembership = () => {
  return useMutation({
    mutationFn: async (variables: {
      filter: string;
      status: MembershipStatus;
      role?: GroupMemberRole;
    }) => {
      const client = await gqlClient();
      return client.request(UPDATE_GROUP_MEMBERSHIP_MUTATION, variables);
    },
  });
};

export const useGetGroupMemberAvatars = (groupId: string, limit = 3) => {
  return useQuery({
    queryKey: ["getGroupMemberAvatars", groupId, limit],
    queryFn: async (): Promise<GroupMemberAvatar[]> => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUP_MEMBERS_QUERY, {
        filter: JSON.stringify({
          groupId,
          status: MembershipStatus.ACTIVE,
        }),
        sort: "{}",
        pageNo: 1,
        pageSize: limit,
      });
      const items = (result?.getgroupmemberships?.items ?? []) as {
        userId: string;
      }[];
      const userIds = items.map((item) => item.userId).filter(Boolean);
      const usersMap = await fetchBlocksUsersByIds(userIds);
      return userIds.map((userId) => {
        const user = usersMap[userId];
        return {
          userId,
          profileImageUrl: user?.profileImageUrl ?? null,
          name: user
            ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
            : "",
        };
      });
    },
    enabled: !!groupId,
  });
};

export const useGetGroupMemberCount = (groupId: string) => {
  return useQuery({
    queryKey: ["getGroupMemberCount", groupId],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUP_MEMBERS_QUERY, {
        filter: JSON.stringify({
          groupId,
          status: MembershipStatus.ACTIVE,
        }),
      });
      return result.getgroupmemberships.totalCount as number;
    },
    enabled: !!groupId,
  });
};

export const useUpdateGroupMemberCount = () => {
  const { mutateAsync: updateGroup } = useUpdateGroup();

  return useMutation({
    mutationFn: async (groupId: string) => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUP_MEMBERS_QUERY, {
        filter: JSON.stringify({ groupId }),
      });
      const totalCount = result.getgroupmemberships.totalCount;
      return updateGroup({
        filter: JSON.stringify({ ItemId: groupId }),
        memberCount: totalCount,
      });
    },
  });
};

export const useGetMyMembership = (groupId: string, userId: string) => {
  return useQuery({
    queryKey: ["getMyMembership", groupId, userId],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUP_MEMBERS_QUERY, {
        filter: JSON.stringify({ groupId, userId }),
        sort: "{}",
        pageNo: 1,
        pageSize: 1,
      });
      return result.getgroupmemberships?.items?.[0] ?? null;
    },
    enabled: !!groupId && !!userId,
  });
};


export const useGetAllMyMemberships = (userId: string) => {
  return useQuery({
    queryKey: ["getAllMyMemberships", userId],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_GROUP_MEMBERS_QUERY, {
        filter: JSON.stringify({ userId }),
        sort: "{}",
        pageNo: 1,
        pageSize: 100,
      });
      return result.getgroupmemberships?.items ?? [];
    },
    enabled: !!userId,
  });
};
